<script setup>
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useRouter, useRoute } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';

const props = defineProps({ item: Object, level: Number });

const router = useRouter();
const route = useRoute();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

/** /register 가 /register/current·/register/my 까지 active 되지 않도록 */
function isMenuPathActive(itemTo, currentPath) {
  if (!itemTo || typeof itemTo !== 'string') return false;
  const to = itemTo.replace(/\/+$/, '') || '/';
  const path = (currentPath || '').replace(/\/+$/, '') || '/';
  if (path === to) return true;
  if (to === '/register') {
    return (
      path.startsWith('/register/')
      && !path.startsWith('/register/current')
      && !path.startsWith('/register/my')
    );
  }
  return path === to || path.startsWith(`${to}/`);
}

const isActive = computed(() => {
  if (props.item?.type === 'external' || !props.item?.to) return false;
  return isMenuPathActive(props.item.to, route.path);
});

// 언어 변경 시 자동으로 업데이트되도록 computed로 감싸기
const menuTitle = computed(() => {
  const translationKey = 'menu.' + props.item.title;
  const translated = t(translationKey);
  // 번역 키가 그대로 반환된 경우 (번역을 찾지 못한 경우) 원본 title 사용
  if (translated === translationKey || translated.startsWith('menu.')) {
    return props.item.title;
  }
  return translated || props.item.title;
});

// 현재 메뉴 클릭 시 리로드 처리
const handleClick = (e) => {
  if (props.item.type === 'external') return;

  // 현재 경로와 클릭한 메뉴의 경로가 같으면 리로드
  if (route.path === props.item.to) {
    e.preventDefault();
    router.go(0);
  }
};
</script>

<template>
    <!---Single Item — custom slot 으로 active 를 직접 제어 (router 기본 prefix 매칭 방지)-->
    <router-link
        :to="`${item.to}`"
        custom
        v-slot="{ href, navigate }"
    >
        <a
            :href="href"
            class="navItemLink single-link rounded-pill"
            :class="{ 'router-link-active router-link-exact-active': isActive }"
            :aria-disabled="item.disabled ? 'true' : undefined"
            @click="(e) => { handleClick(e); if (!e.defaultPrevented && !item.disabled) navigate(e); }"
        >
            <!---If icon-->
            <i class="navIcon">
                <Icon :icon="'solar:' + item.icon" height="18" width="18" :level="level" class="dot" :class="'text-' + item.BgColor"/>
            </i>
            <span>{{ menuTitle }}</span>
            <!---If Caption-->
            <small v-if="item.subCaption" class="text-caption mt-n1 hide-menu">
                {{ item.subCaption }}
            </small>
            <!---If any chip or label-->
            <template v-if="item.chip">
                <v-chip
                    :color="item.chipColor"
                    class="sidebarchip hide-menu ml-auto"
                    :size="item.chipIcon ? 'small' : 'small'"
                    :variant="item.chipVariant"
                    :prepend-icon="item.chipIcon"
                >
                    {{ item.chip }}
                </v-chip>
            </template>
        </a>
    </router-link>
</template>
